import React from "react";
import { Input } from "@/components/ui/input";
import { numberToWordsIN } from "@/lib/numberToWords";

/**
 * AmountInput — numeric Input that shows the typed value in Indian words.
 *
 * Two layout modes:
 *   • variant="inline" (DEFAULT) — words label flows below the input in
 *     normal document order. Ideal for stacked forms (Rectify dialogs,
 *     side-panels, etc.).
 *   • variant="floating" — words label is absolutely positioned below.
 *     Ideal for tightly packed `grid items-end` rows where the sibling
 *     cells must stay aligned with the input's baseline (Record Payments,
 *     Record Cash, Upload for Party rows on the admin dashboard).
 *
 * Props are forwarded verbatim to the underlying <Input>.
 * Extra optional props:
 *   • showWords (default true)
 *   • wordsClassName — extra classes for the words label.
 */
const AmountInput = React.forwardRef(function AmountInput(
    {
        value,
        showWords = true,
        wordsClassName = "",
        wordsStyle,
        variant = "inline",
        "data-testid": testId,
        ...rest
    },
    ref,
) {
    const words = numberToWordsIN(value);
    const hasValue = value !== "" && value !== null && value !== undefined && Number(value) > 0;

    const wordsEl = showWords && hasValue && (
        <div
            data-testid={testId ? `${testId}-words` : "amount-words"}
            title={words}
            style={wordsStyle}
            className={
                variant === "floating"
                    ? `pointer-events-none absolute left-0 right-0 top-full mt-0.5 truncate px-0.5 text-[10px] italic leading-tight text-slate-500 ${wordsClassName}`
                    : `mt-1 px-0.5 text-[11px] italic leading-tight text-slate-500 ${wordsClassName}`
            }
        >
            {words}
        </div>
    );

    return (
        <div className={variant === "floating" ? "relative" : ""}>
            <Input
                ref={ref}
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={value ?? ""}
                data-testid={testId}
                {...rest}
            />
            {wordsEl}
        </div>
    );
});

export default AmountInput;
